'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

interface ImageElementProps {
  src: string;
  alt: string;
  className?: string;
  scroller?: string;
  priority?: boolean;
}

const ImageElement = ({ src, alt, className, scroller, priority }: ImageElementProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [animatedSrc, setAnimatedSrc] = useState<string | null>(null);
  const isLoaded = loadedSrc === src;
  const isAnimated = animatedSrc === src;

  const markLoadedIfCurrent = (img: HTMLImageElement | null) => {
    const currentSrc = img?.currentSrc ? decodeURIComponent(img.currentSrc) : '';
    const isCurrentSrc = currentSrc.includes(src) || img?.currentSrc.endsWith(src);

    if (img?.complete && img.naturalWidth > 0 && isCurrentSrc) {
      setLoadedSrc(src);
    }
  };

  useEffect(() => {
    setAnimatedSrc(null);
    markLoadedIfCurrent(imgRef.current);
  }, [src]);

  useGSAP(
    () => {
      if (!isLoaded || !imgRef.current) {
        return;
      }

      gsap.fromTo(
        imgRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          onComplete: () => setAnimatedSrc(src),
          scrollTrigger: {
            trigger: imgRef.current,
            start: 'top 90%',
            scroller: scroller,
          },
        }
      );
    },
    { dependencies: [isLoaded, scroller, src], revertOnUpdate: true }
  );

  return (
    <Image
      ref={imgRef}
      alt={alt}
      className={className}
      src={src}
      width={2400}
      height={1404}
      sizes="100vw"
      priority={priority}
      unoptimized={src.endsWith('.gif')}
      onLoad={(event) => markLoadedIfCurrent(event.currentTarget)}
      style={{
        width: '100%',
        height: 'auto',
        opacity: isAnimated ? undefined : 0,
      }}
    />
  );
};

export default ImageElement;
